<template>
  <v-row class="mt-2">
    <v-col class="py-0" cols="12">
      <v-alert tile type="error" v-if="error">{{ error }}</v-alert>
    </v-col>
    <v-col cols="12" md="2" sm="6">
      <v-text-field
        @click:append-outer="refresh()"
        @click:clear="clearSearch()"
        @keydown.enter="refresh()"
        clearable
        dense
        hide-details
        outlined
        required
        :label="$t('datatable.searchLabel')"
        v-model="search"
      ></v-text-field>
    </v-col>

    <v-spacer />
    <v-col align="right" cols="12" md="2" sm="6">
      <v-btn @click="resetFilters()" color="primary" dark rounded small text>
        {{ $t("button.clearFiltersBtn") }}
      </v-btn>
    </v-col>

    <v-col cols="12" md="12">
      <v-data-table
        :headers="headers"
        :loading="$apollo.queries.reportGroups.loading"
        :items="reportGroups.edges"
        :options.sync="dataTableOptions"
        :server-items-length="reportGroups.totalCount"
        :footer-props="{
          'disable-pagination': $apollo.queries.reportGroups.loading,
          'items-per-page-options': [10, 25, 50, 75, 100],
        }"
        :show-select="'show-select'"
        item-key="node.id"
        v-model="selectedItems"
      >
        <template v-slot:[`item.action`]="{ item }">
          <v-btn @click="edit(item.node)" color="primary" icon>
            <v-icon>edit</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-col>

    <DeleteDialog
      :dialog.sync="deleteDialog"
      v-on:confirmed="onDeleteConfirmed"
    />

    <ReportGroupEditDialog
      :dialog.sync="editDialog"
      :object.sync="editedItem"
      v-on:changed="onChanged"
    />
  </v-row>
</template>

<script>
import DeleteDialog from "@/components/base/DeleteDialog.vue";
import gql from "graphql-tag";
import helper from "@/utils/helper.js";
import ReportGroupEditDialog from "@/components/reports/ReportGroupEditDialog.vue";

export default {
  name: "report-groups-list",
  components: {
    DeleteDialog,
    ReportGroupEditDialog,
  },

  apollo: {
    reportGroups: {
      query: gql`
        query reportGroups(
          $first: Int
          $last: Int
          $before: String
          $after: String
          $search: String
          $orderBy: [String]
        ) {
          reportGroups(
            first: $first
            last: $last
            before: $before
            after: $after
            search: $search
            orderBy: $orderBy
          ) {
            edgeCount
            totalCount
            pageInfo {
              startCursor
              endCursor
              hasPreviousPage
              hasNextPage
            }
            edges {
              node {
                id
                reportGroupName
              }
            }
          }
        }
      `,
      variables() {},
      fetchPolicy: "cache-and-network",
      update: (data) => data.reportGroups,
      skip: true,
    },
  },

  data: function () {
    return {
      deleteDialog: false,
      editDialog: false,
      editedItem: {},
      error: null,
      selectedItems: [],
      reportGroups: {},

      headers: [
        {
          text: this.$t(
            "reportsSettings.reportGroups.datatableHeaders.reportGroupName"
          ),
          value: "node.reportGroupName",
          sortable: true,
        },
        {
          text: this.$t("datatable.actionHeader"),
          align: "center",
          value: "action",
          sortable: false,
        },
      ],
    };
  },

  computed: {
    me() {
      return this.$store.state.user.me;
    },

    dataTableOptions: {
      get() {
        return this.$store.getters["reportGroupsList/getDataTableOptions"];
      },
      set(value) {
        this.$store.dispatch("reportGroupsList/setDataTableOptions", value);
      },
    },

    search: {
      get() {
        return this.$store.getters["reportGroupsList/getSearch"];
      },
      set(value) {
        this.$store.dispatch("reportGroupsList/setSearch", value);
      },
    },
  },

  watch: {
    dataTableOptions() {
      this.refresh();
    },

    selectedItems() {
      this.$emit("updateNrOfSelectedItems", this.selectedItems.length);
    },
  },

  created() {
    this.hasPermission = helper.hasPermission;
    this.orderByList = helper.orderByList;
  },

  mounted() {},

  methods: {
    add() {
      this.editedItem = null;
      this.editDialog = true;
    },

    clearSearch() {
      this.search = null;
      this.refresh();
    },

    deleteSelected() {
      this.deleteDialog = true;
    },

    edit(item) {
      this.editedItem = item;
      this.editDialog = true;
    },

    onChanged() {
      this.$apollo.queries.reportGroups.refresh();
    },

    onDeleteConfirmed() {
      var ids = [];
      this.selectedItems.forEach(function (item, index) {
        ids.push(item.node.id);
      });

      const payload = {
        ids: ids,
      };

      this.$apollo
        .mutate({
          mutation: gql`
            mutation deleteReportGroups($input: DeleteReportGroupsInput!) {
              deleteReportGroups(input: $input) {
                reportGroups {
                  id
                }
              }
            }
          `,
          variables: {
            input: payload,
          },
        })
        .then(() => {
          this.$apollo.queries.reportGroups.refresh();

          this.deleteDialog = false;
          this.selectedItems = [];

          const payload = {
            color: "success",
            message: `ReportGroups successfully deleted`,
          };
          this.$store.dispatch("snackbar/showMessage", payload);
        })
        .catch((error) => {
          this.error = error;
        });
    },

    refresh() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.dataTableOptions;

      var _first = itemsPerPage;
      var _last = null;
      var _before = null;
      var _after = null;

      if (page < this.page) {
        _first = null;
        _last = itemsPerPage;
        _before = this.reportGroups.pageInfo.startCursor;
        _after = null;
      }

      if (page > this.page) {
        _first = itemsPerPage;
        _last = null;
        _before = null;
        _after = this.reportGroups.pageInfo.endCursor;
      }

      var orderByList = this.orderByList(sortBy, sortDesc);

      this.$apollo.queries.reportGroups.setVariables({
        first: _first,
        last: _last,
        before: _before,
        after: _after,
        orderBy: orderByList,
        search: this.search,
        report_Tenant: this.selectedReportTenant,
        user_Tenant: this.selectedUserTenant,
      });

      this.$apollo.queries.reportGroups.skip = false;
      this.$apollo.queries.reportGroups.refresh();

      this.page = page;
    },

    resetFilters() {
      this.clearSearch();
    },
  },
};
</script>
